import { extractVariables } from './email-html';

/**
 * Import-time normalisation for production email HTML.
 *
 * Every rule here is either a SECURITY removal or a RENDERING-PARITY addition.
 * None of them changes how the template looks — that is the hard constraint:
 * an imported template must render visually identically to the source file.
 *
 * Explicitly NOT done: simplifying layout, removing sections, unwrapping nested
 * tables, stripping `<html>/<head>/<body>`, rewriting URLs, or reformatting.
 * Production email HTML depends on all of those.
 *
 * The normaliser is string-based rather than DOM-based on purpose. Round-tripping
 * through `DOMParser` + `serialize` silently rewrites the document — it injects
 * `<tbody>`, re-orders and lower-cases attributes, re-encodes entities and drops
 * Outlook conditional comments — which would break byte-stability and, with
 * conditional comments, actually change Outlook rendering.
 */

export interface NormalizationChange {
  rule: string;
  detail: string;
  /** How many occurrences were affected. */
  count: number;
}

export interface NormalizationResult {
  html: string;
  changes: NormalizationChange[];
  /** Variables still unknown to the registry after aliasing. */
  unknownVariables: string[];
}

/**
 * Aliases for variables that templates commonly use under a different name than
 * the registry key. These map to EXISTING registry variables — no variable is
 * invented here. An unaliased unknown key is reported, never silently changed.
 */
const VARIABLE_ALIASES: Record<string, string> = {
  company_logo: 'company_logo_url',
  logo_url: 'company_logo_url',
  company_site: 'company_website',
  support_hours_text: 'support_hours',
  login_url: 'portal_login_url',
  portal_url_login: 'portal_login_url',
};

/** Tags removed outright — script-capable or unrenderable in email. */
const STRIP_TAGS = ['script', 'iframe', 'object', 'embed', 'applet', 'form', 'base'] as const;

export function normalizeEmailHtml(
  source: string,
  registryKeys: readonly string[] = [],
): NormalizationResult {
  let html = source;
  const changes: NormalizationChange[] = [];

  const record = (rule: string, detail: string, count: number) => {
    if (count > 0) changes.push({ rule, detail, count });
  };

  // ── 1. Security: remove script-capable elements, content and all ───────────
  for (const tag of STRIP_TAGS) {
    const paired = new RegExp(`<${tag}\\b[^>]*>[\\s\\S]*?<\\/${tag}>`, 'gi');
    const selfClosing = new RegExp(`<${tag}\\b[^>]*\\/?>`, 'gi');
    const before = html;
    html = html.replace(paired, '').replace(selfClosing, '');
    if (html !== before) {
      const count = (before.match(new RegExp(`<${tag}\\b`, 'gi')) ?? []).length;
      record('strip-unsupported-tag', `Removed <${tag}> (not permitted in email)`, count);
    }
  }

  // ── 2. Security: meta refresh redirects. charset/viewport are KEPT — they are
  //      required for correct rendering in Gmail and Apple Mail. ──────────────
  {
    const refresh = /<meta\b[^>]*\bhttp-equiv\s*=\s*["']?\s*refresh[^>]*>/gi;
    const count = (html.match(refresh) ?? []).length;
    html = html.replace(refresh, '');
    record('strip-meta-refresh', 'Removed <meta http-equiv="refresh"> redirect', count);
  }

  // ── 3. Security: inline event handlers and javascript: URLs ────────────────
  {
    const handlers = /\son[a-z]+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi;
    const count = (html.match(handlers) ?? []).length;
    html = html.replace(handlers, '');
    record('strip-event-handlers', 'Removed inline on* event handlers', count);
  }
  {
    const jsUrl = /((?:href|src)\s*=\s*)(["'])\s*javascript:[^"']*\2/gi;
    const count = (html.match(jsUrl) ?? []).length;
    html = html.replace(jsUrl, '$1$2#$2');
    record('neutralise-javascript-url', 'Replaced javascript: URL with #', count);
  }

  // ── 4. Outlook parity: <table> without an explicit border renders a 1px rule
  //      in some Outlook builds. border="0" is the CSS default, so adding it
  //      changes nothing visually anywhere else. ──────────────────────────────
  {
    let count = 0;
    html = html.replace(/<table\b([^>]*)>/gi, (whole, attrs: string) => {
      if (/\bborder\s*=/i.test(attrs)) return whole;
      count += 1;
      return `<table${attrs} border="0">`;
    });
    record('outlook-table-border', 'Added border="0" to <table> for Outlook parity', count);
  }

  // ── 5. Outlook parity: a linked <img> without border="0" draws a blue frame
  //      in Outlook and older Yahoo. Again the CSS default, so visually inert. ─
  {
    let count = 0;
    html = html.replace(/<img\b([^>]*?)(\/?)>/gi, (whole, attrs: string, selfClose: string) => {
      if (/\bborder\s*=/i.test(attrs)) return whole;
      count += 1;
      return `<img${attrs} border="0"${selfClose}>`;
    });
    record('outlook-img-border', 'Added border="0" to <img> for Outlook parity', count);
  }

  // ── 6. Accessibility / blocked-image fallback: <img> needs alt text. Added
  //      empty only when absent, which is visually inert. ────────────────────
  {
    let count = 0;
    html = html.replace(/<img\b([^>]*?)(\/?)>/gi, (whole, attrs: string, selfClose: string) => {
      if (/\balt\s*=/i.test(attrs)) return whole;
      count += 1;
      return `<img${attrs} alt=""${selfClose}>`;
    });
    record('add-img-alt', 'Added alt="" to <img> without alt text', count);
  }

  // ── 7. Variables: map known synonyms onto registry keys. Nothing invented. ─
  {
    for (const [from, to] of Object.entries(VARIABLE_ALIASES)) {
      const pattern = new RegExp(`\\{\\{\\s*${from}\\s*\\}\\}`, 'g');
      const count = (html.match(pattern) ?? []).length;
      if (count > 0) {
        html = html.replace(pattern, `{{${to}}}`);
        record('alias-variable', `{{${from}}} → {{${to}}} (registry key)`, count);
      }
    }
  }

  // ── 8. Whitespace inside variable braces breaks exact-match substitution. ──
  {
    const loose = /\{\{\s+([a-zA-Z_][a-zA-Z0-9_]*)\s*\}\}|\{\{\s*([a-zA-Z_][a-zA-Z0-9_]*)\s+\}\}/g;
    const count = (html.match(loose) ?? []).length;
    html = html.replace(loose, (_m, a: string, b: string) => `{{${a ?? b}}}`);
    record('tighten-variable-braces', 'Removed whitespace inside {{ }}', count);
  }

  // Anything still unknown is REPORTED, never guessed at.
  const unknownVariables =
    registryKeys.length > 0 ? extractVariables(html).filter((k) => !registryKeys.includes(k)) : [];

  return { html, changes, unknownVariables };
}
